// src/modules/stock/StockOutflowPage.js

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebaseConfig';
import {
    collection,
    getDocs,
    doc,
    runTransaction,
    serverTimestamp,
    query,
    where,
    orderBy,
    onSnapshot,
} from 'firebase/firestore';

// React Icons
import {
    FiPlus,
    FiX,
    FiSave,
    FiEdit,
    FiTrash2,
    FiArrowLeft,
    FiArrowRight,
    FiRefreshCw,
    FiCheck,
    FiAlertCircle,
    FiInfo,
    FiPackage,
    FiTag,
    FiClipboard,
    FiSearch,
    FiChevronDown,
    FiChevronUp,
    FiFolder,
    FiCalendar,
} from 'react-icons/fi';
import { MdCancel, MdEdit, MdDelete, MdAdd, MdSave, MdLocationOn } from 'react-icons/md';
import { FaBox, FaTags, FaClipboardList } from 'react-icons/fa';
import { HiOutlineSwitchVertical } from "react-icons/hi";

import useAuth from '../../hooks/useAuth';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    FormModal,
    ConfirmModal,
    SearchBar,
    Pagination,
    TableSkeleton,
    EmptyState,
    Toast,
    PageHeader
} from '../../components/common';

import styles from './Logistics.module.css';

const ITEMS_PER_PAGE = 10;
const DEBOUNCE_DELAY = 300;
const STOCK_OUTFLOWS_COLLECTION = 'stock-outflows';
const CURRENT_STOCK_COLLECTION = 'current-stock';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const PRODUCTS_COLLECTION = 'products';
const PRODUCT_TYPE_COLLECTION_PATH = 'parameters/types/tipo-produto';
const CATEGORY_COLLECTION_PATH = 'parameters/data/product-categories';
const LOCATIONS_COLLECTION = 'localizacoes';

// Helper para verificar se está expirado
const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    try {
        const expiry = expirationDate.toDate ? expirationDate.toDate() : new Date(expirationDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return expiry < today;
    } catch {
        return false;
    }
};

// Helper para formatar data
const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
        const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
        return date.toLocaleDateString('pt-BR');
    } catch {
        return null;
    }
};

// Hook de debounce personalizado
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
};

// Cache service
class CacheService {
    constructor(ttl = 300000) {
        this.cache = new Map();
        this.ttl = ttl;
    }

    get(key) {
        const cached = this.cache.get(key);
        if (cached && Date.now() - cached.timestamp < this.ttl) {
            return cached.data;
        }
        return null;
    }

    set(key, data) {
        this.cache.set(key, { data, timestamp: Date.now() });
    }
}

const cache = new CacheService();

// --- Componente de Conteúdo do Modal de Saída ---
const StockOutflowModalContent = ({
    isEditing,
    itemsList,
    locationsList,
    selectedItemId,
    setSelectedItemId,
    selectedLocationIds,
    setSelectedLocationIds,
    quantity,
    setQuantity,
    notes,
    setNotes,
    status,
    productTypes,
    categories,
    filterProductTypeId,
    setFilterProductTypeId,
    filterCategoryId,
    setFilterCategoryId,
    selectedBatch,
    setSelectedBatch,
    loading,
    t
}) => {
    const [itemSearchTerm, setItemSearchTerm] = useState('');
    const [isItemOpen, setIsItemOpen] = useState(false);
    const [availableStock, setAvailableStock] = useState({});
    const [availableLocations, setAvailableLocations] = useState([]);
    const [stockLoading, setStockLoading] = useState(false);
    const [quantityErrors, setQuantityErrors] = useState({});
    const [showBatchSelector, setShowBatchSelector] = useState(false);

    // Refs para posicionamento dos dropdowns
    const itemInputRef = useRef(null);
    const modalContentRef = useRef(null);

    // Estado para posicionamento do dropdown
    const [itemDropdownPos, setItemDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    const selectedItem = itemsList?.find(r => r.id === selectedItemId);

    // Função para atualizar posição do dropdown
    const updateDropdownPosition = () => {
        if (itemInputRef.current) {
            const rect = itemInputRef.current.getBoundingClientRect();
            setItemDropdownPos({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
    };

    // Função utilitária para formatar quantidade
    const formatQuantity = (value) => {
        if (value === undefined || value === null || isNaN(value)) {
            return '0.000';
        }
        return value.toFixed(3);
    };

    // Buscar saldo disponível com onSnapshot em tempo real (incluindo lote)
    useEffect(() => {
        if (!selectedItemId) {
            setAvailableStock({});
            setAvailableLocations([]);
            setSelectedBatch(null);
            return;
        }

        setStockLoading(true);

        const stockQuery = query(
            collection(db, CURRENT_STOCK_COLLECTION),
            where("rawMaterialId", "==", selectedItemId),
            where("quantity", ">", 0.001)
        );

        const unsubscribe = onSnapshot(stockQuery, (snapshot) => {
            const stocks = {};
            const locationsWithStock = [];

            snapshot.docs.forEach(doc => {
                const data = doc.data();
                if (data.rawMaterialId === selectedItemId) {
                    stocks[data.locationId] = {
                        quantity: data.quantity || 0,
                        lotNumber: data.lotNumber || null,
                        expirationDate: data.expirationDate || null,
                        manufacturerDate: data.manufacturerDate || null,
                        stockId: doc.id,
                        isProduct: data.isProduct || false
                    };
                    locationsWithStock.push(data.locationId);
                }
            });

            setAvailableStock(stocks);
            setAvailableLocations(locationsWithStock);
            setStockLoading(false);
        }, (error) => {
            console.error("Erro ao buscar saldos:", error);
            setStockLoading(false);
        });

        return () => unsubscribe();
    }, [selectedItemId, setSelectedBatch]);

    // Group stock by batch/lot number when item is selected
    const getBatchesFromStock = useCallback(() => {
        if (!selectedItemId || Object.keys(availableStock).length === 0) {
            return [];
        }

        // Group stock by batch/lot number
        const batchesMap = new Map();

        Object.entries(availableStock).forEach(([locationId, stockInfo]) => {
            const batchKey = stockInfo.lotNumber || 'NO_LOT';

            if (!batchesMap.has(batchKey)) {
                batchesMap.set(batchKey, {
                    lotNumber: stockInfo.lotNumber,
                    expirationDate: stockInfo.expirationDate,
                    manufacturerDate: stockInfo.manufacturerDate,
                    totalQuantity: 0,
                    locations: []
                });
            }

            const batch = batchesMap.get(batchKey);
            batch.totalQuantity += stockInfo.quantity;
            batch.locations.push({
                locationId,
                quantity: stockInfo.quantity,
                stockId: stockInfo.stockId,
                expirationDate: stockInfo.expirationDate
            });
        });

        const batches = Array.from(batchesMap.values());
        batches.sort((a, b) => {
            // Sort expired batches last
            const aExpired = a.expirationDate && isExpired(a.expirationDate);
            const bExpired = b.expirationDate && isExpired(b.expirationDate);
            if (aExpired && !bExpired) return 1;
            if (!aExpired && bExpired) return -1;
            // Put "NO_LOT" at the end
            if (!a.lotNumber && b.lotNumber) return 1;
            if (a.lotNumber && !b.lotNumber) return -1;
            return (a.lotNumber || '').localeCompare(b.lotNumber || '');
        });

        return batches;
    }, [availableStock, selectedItemId]);

    // Auto-select batch when only one is available
    useEffect(() => {
        const batches = getBatchesFromStock();

        if (batches.length === 1) {
            setSelectedBatch(batches[0]);
            setShowBatchSelector(false);
        } else if (batches.length > 1) {
            setShowBatchSelector(true);
            setSelectedBatch(null);
        } else if (batches.length === 0 && Object.keys(availableStock).length > 0) {
            // Has stock but no batch info (all null lot numbers)
            setSelectedBatch(null);
            setShowBatchSelector(true);
        }
    }, [availableStock, getBatchesFromStock, setSelectedBatch]);

    // Reset location selection when batch changes
    useEffect(() => {
        if (selectedBatch) {
            setSelectedLocationIds([]);
            setQuantity({});
        }
    }, [selectedBatch, setSelectedLocationIds, setQuantity]);

    // Validação em tempo real das quantidades
    useEffect(() => {
        const errors = {};
        selectedLocationIds.forEach(locId => {
            const batchLocation = selectedBatch?.locations.find(l => l.locationId === locId);
            const currentBalance = batchLocation?.quantity || 0;
            const qtyValue = parseFloat(quantity[locId]);

            if (quantity[locId] && !isNaN(qtyValue)) {
                if (qtyValue <= 0) {
                    errors[locId] = t('pages.stock.outflow.quantityGreaterThanZero', 'A quantidade deve ser maior que zero');
                } else if (qtyValue > currentBalance) {
                    errors[locId] = t('pages.stock.outflow.insufficientBalance', 'Saldo insuficiente neste lote. Disponível: {balance}', { balance: formatQuantity(currentBalance) });
                } else if (qtyValue.toString().split('.')[1]?.length > 3) {
                    errors[locId] = t('pages.stock.outflow.maxDecimals', 'Máximo de 3 casas decimais');
                }
            } else if (selectedLocationIds.length > 0 && (!quantity[locId] || quantity[locId] === '')) {
                errors[locId] = t('pages.stock.outflow.quantityRequired', 'Quantidade obrigatória');
            }
        });
        setQuantityErrors(errors);
    }, [quantity, selectedBatch, selectedLocationIds, t]);

    // Filtrar itens com base nos filtros de Tipo e Categoria
    const filteredItems = useMemo(() => {
        if (!itemsList) return [];
        const lowerCaseSearchTerm = (itemSearchTerm || '').toLowerCase();

        const filtered = itemsList.filter(item => {
            const itemCode = (item.code || '').toLowerCase();
            const itemName = (item.commercialName || item.name || '').toLowerCase();

            const matchesSearch = itemCode.includes(lowerCaseSearchTerm) ||
                itemName.includes(lowerCaseSearchTerm);

            if (!matchesSearch) return false;

            const matchesType = !filterProductTypeId || (item.productTypeIds && item.productTypeIds.includes(filterProductTypeId));
            const matchesCategory = !filterCategoryId || (item.categoryIds && item.categoryIds.includes(filterCategoryId));

            return matchesType && matchesCategory;
        });

        return filtered.slice(0, 50);
    }, [itemsList, itemSearchTerm, filterProductTypeId, filterCategoryId]);

    // Available locations only from selected batch
    const availableLocationsForBatch = useMemo(() => {
        if (!selectedBatch || !locationsList) return [];

        const batchLocationIds = new Set(selectedBatch.locations.map(l => l.locationId));

        return locationsList
            .filter(loc =>
                batchLocationIds.has(loc.id) &&
                !selectedLocationIds.includes(loc.id)
            )
            .sort((a, b) => (a.description || '').localeCompare(b.description || ''));
    }, [selectedBatch, locationsList, selectedLocationIds]);

    const itemDisplayValue = selectedItem
        ? `${selectedItem.code} - ${selectedItem.commercialName || selectedItem.name}`
        : itemSearchTerm;

    const handleSelectItem = (option) => {
        setSelectedItemId(option.id);
        setItemSearchTerm('');
        setIsItemOpen(false);
        setSelectedLocationIds([]);
        setQuantity({});
        setSelectedBatch(null);
        setShowBatchSelector(false);
    };

    const handleBatchSelect = (batch) => {
        setSelectedBatch(batch);
        setShowBatchSelector(false);
        setSelectedLocationIds([]);
        setQuantity({});
    };

    const handleItemInputChange = (e) => {
        const newTerm = e.target.value;
        setItemSearchTerm(newTerm);
        if (selectedItemId && selectedItem) {
            const itemName = selectedItem.commercialName || selectedItem.name || '';
            if (
                !(newTerm.toLowerCase().includes(selectedItem.code.toLowerCase())) &&
                !(newTerm.toLowerCase().includes(itemName.toLowerCase()))
            ) {
                setSelectedItemId('');
                setSelectedBatch(null);
            }
        }
        setIsItemOpen(true);
    };

    const handleItemFocus = () => {
        updateDropdownPosition();
        setIsItemOpen(true);
    };

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        function handleClickOutside(event) {
            if (itemInputRef.current && !itemInputRef.current.contains(event.target)) {
                setIsItemOpen(false);
                if (!selectedItemId) {
                    setItemSearchTerm('');
                }
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [selectedItemId]);

    // Atualizar posição do dropdown quando abrir
    useEffect(() => {
        if (isItemOpen) {
            updateDropdownPosition();
        }
    }, [isItemOpen]);

    // Fechar dropdown ao rolar o modal
    useEffect(() => {
        if (!isItemOpen) return;

        const handleScroll = () => {
            setIsItemOpen(false);
        };

        const modalContainer = modalContentRef.current;
        if (modalContainer) {
            modalContainer.addEventListener('scroll', handleScroll);
            return () => modalContainer.removeEventListener('scroll', handleScroll);
        }
    }, [isItemOpen]);

    const handleRemoveLocation = (locId) => {
        setSelectedLocationIds(prev => prev.filter(id => id !== locId));
        setQuantity(prev => {
            const newQty = { ...prev };
            delete newQty[locId];
            return newQty;
        });
    };

    const handleQuantityChange = (locId, value) => {
        setQuantity(prev => ({ ...prev, [locId]: value }));
    };

    const totalOutflow = selectedLocationIds.reduce((acc, id) => acc + (parseFloat(quantity[id]) || 0), 0);

    const isFormValid = () => {
        if (!selectedItemId) return false;
        if (!selectedBatch) return false;
        if (selectedLocationIds.length === 0) return false;
        if (totalOutflow <= 0) return false;
        return Object.keys(quantityErrors).length === 0;
    };

    const currentBatches = getBatchesFromStock();

    return (
        <div ref={modalContentRef}>
            {/* Seção 1: Filtros de Tipo e Categoria */}
            {!isEditing && (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>
                            <FiSearch size={20} />
                        </div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.outflow.filtersSection', 'Filtros')}</h3>
                        <span className={styles.sectionBadge}>{t('pages.stock.outflow.optionalBadge', 'Opcional')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup}>
                            <label htmlFor="filterType">{t('pages.stock.outflow.filterByType', 'Filtrar por Tipo')}</label>
                            <select
                                id="filterType"
                                value={filterProductTypeId}
                                onChange={(e) => {
                                    setFilterProductTypeId(e.target.value);
                                    setSelectedItemId('');
                                    setItemSearchTerm('');
                                    setSelectedBatch(null);
                                }}
                                className={`${styles.input} ${styles.select}`}
                                disabled={loading}
                            >
                                <option value="">{t('pages.stock.outflow.allTypes', 'Todos os Tipos')}</option>
                                {productTypes && productTypes.map(type => (
                                    <option key={type.id} value={type.id}>{type.name}</option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="filterCategory">{t('pages.stock.outflow.filterByCategory', 'Filtrar por Categoria')}</label>
                            <select
                                id="filterCategory"
                                value={filterCategoryId}
                                onChange={(e) => {
                                    setFilterCategoryId(e.target.value);
                                    setSelectedItemId('');
                                    setItemSearchTerm('');
                                    setSelectedBatch(null);
                                }}
                                className={`${styles.input} ${styles.select}`}
                                disabled={loading}
                            >
                                <option value="">{t('pages.stock.outflow.allCategories', 'Todas as Categorias')}</option>
                                {categories && categories.map(category => (
                                    <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            {/* Seção 2: Item (Matéria-Prima/Produto) */}
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                        <FiPackage size={20} />
                    </div>
                    <h3 className={styles.sectionTitle}>{t('pages.stock.outflow.itemSection', 'Item')}</h3>
                    <span className={styles.sectionBadge}>{t('common.required', 'Obrigatório')}</span>
                </div>
                <div className={styles.formGrid}>
                    <div className={styles.inputGroup} style={{ gridColumn: 'span 2', position: 'relative' }}>
                        <label>
                            {t('pages.stock.outflow.itemLabel', 'Item')} <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.searchableSelectWrapper} style={{ position: 'relative' }}>
                            <input
                                ref={itemInputRef}
                                type="text"
                                className={`${styles.input} ${styles.comboInput}`}
                                placeholder={t('pages.stock.outflow.itemPlaceholder', 'Buscar por código ou nome...')}
                                value={isItemOpen ? itemSearchTerm : itemDisplayValue}
                                onChange={handleItemInputChange}
                                onFocus={handleItemFocus}
                                disabled={loading || isEditing}
                            />
                            <div
                                className={`${styles.dropdownArrow} ${isItemOpen ? styles.open : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isEditing) {
                                        if (!isItemOpen) {
                                            updateDropdownPosition();
                                        }
                                        setIsItemOpen(!isItemOpen);
                                        if (isItemOpen) setItemSearchTerm('');
                                    }
                                }}
                            >
                                {isItemOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                            </div>
                            {isItemOpen && !isEditing && itemDropdownPos.width > 0 && (
                                <div
                                    className={styles.optionsDropdownPortal}
                                    style={{
                                        position: 'fixed',
                                        top: itemDropdownPos.top,
                                        left: itemDropdownPos.left,
                                        width: itemDropdownPos.width,
                                        zIndex: 10001,
                                        maxHeight: '250px',
                                        overflowY: 'auto',
                                        backgroundColor: 'var(--content-bg)',
                                        border: '2px solid var(--border-color)',
                                        borderRadius: 'var(--border-radius-md)',
                                        boxShadow: 'var(--shadow-lg)'
                                    }}
                                >
                                    {loading && <div className={styles.loadingOption}>{t('common.loading', 'Carregando opções...')}</div>}

                                    {!loading && filteredItems.length > 0 ? (
                                        filteredItems.map(option => {
                                            const isProduct = option.isProduct === true;
                                            return (
                                                <div
                                                    key={option.id}
                                                    className={`${styles.optionItem} ${selectedItemId === option.id ? styles.selected : ''}`}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        handleSelectItem(option);
                                                    }}
                                                    style={{
                                                        padding: '10px 12px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        borderBottom: '1px solid var(--border-color)',
                                                        transition: 'var(--transition-default)'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--row-hover-bg)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                >
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <span className={styles.optionCode}>{option.code}</span>
                                                            <span style={{
                                                                fontSize: '10px',
                                                                padding: '2px 6px',
                                                                borderRadius: '4px',
                                                                backgroundColor: isProduct ? '#003a71' : '#0087b0',
                                                                color: '#ffffff',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                {isProduct ? t('pages.stock.outflow.product', 'PRODUTO') : t('pages.stock.outflow.rawMaterial', 'MATÉRIA-PRIMA')}
                                                            </span>
                                                        </div>
                                                        <span className={styles.optionName}>{option.commercialName || option.name}</span>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : !loading && (
                                        <div className={styles.noResults} style={{ padding: '12px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                            {itemSearchTerm || filterProductTypeId || filterCategoryId
                                                ? t('pages.stock.outflow.noResults', 'Nenhum resultado encontrado para os filtros aplicados.')
                                                : t('pages.stock.outflow.startTyping', 'Comece a digitar para buscar.')}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {!selectedItemId && !isEditing && (
                            <small className={styles.errorTextSmall}>{t('pages.stock.outflow.itemRequired', 'Seleção do Item é obrigatória.')}</small>
                        )}
                    </div>
                </div>
            </div>

            {/* Seção 3: Seleção de Lote */}
            {selectedItemId && !stockLoading && (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <div className={styles.sectionIcon}>
                            <FiTag size={20} />
                        </div>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.outflow.batchSelection', 'Seleção de Lote')}</h3>
                        <span className={styles.sectionBadge}>
                            {currentBatches.length > 0 ? t('common.required', 'Obrigatório') : t('pages.stock.outflow.autoBadge', 'Automático')}
                        </span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            {currentBatches.length === 0 && Object.keys(availableStock).length > 0 ? (
                                <div className={styles.infoTextSmall} style={{
                                    padding: '12px',
                                    background: 'var(--info-light)',
                                    borderRadius: '8px',
                                    textAlign: 'center'
                                }}>
                                    <FiInfo size={16} style={{ display: 'inline', marginRight: '8px' }} />
                                    {t('pages.stock.outflow.noLotStock', 'Este item possui estoque sem número de lote registrado. A saída será processada normalmente.')}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const syntheticBatch = {
                                                lotNumber: null,
                                                expirationDate: null,
                                                manufacturerDate: null,
                                                totalQuantity: Object.values(availableStock).reduce(
                                                    (sum, stock) => sum + (stock.quantity || 0), 0
                                                ),
                                                locations: Object.entries(availableStock).map(([locationId, stockInfo]) => ({
                                                    locationId,
                                                    quantity: stockInfo.quantity,
                                                    stockId: stockInfo.stockId,
                                                    expirationDate: stockInfo.expirationDate
                                                }))
                                            };
                                            handleBatchSelect(syntheticBatch);
                                        }}
                                        className={styles.primaryButton}
                                        style={{ marginTop: '12px', padding: '8px 16px', fontSize: '14px' }}
                                    >
                                        <FiCheck size={14} /> {t('pages.stock.outflow.continueWithoutLot', 'Continuar sem lote →')}
                                    </button>
                                </div>
                            ) : showBatchSelector || !selectedBatch ? (
                                <div className={styles.batchSelectorGrid}>
                                    <label>{t('pages.stock.outflow.selectBatchLabel', 'Selecione o Lote que deseja dar saída:')}</label>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                                        {currentBatches.map((batch, idx) => {
                                            const isBatchExpired = batch.expirationDate && isExpired(batch.expirationDate);
                                            const isNoLot = !batch.lotNumber;
                                            return (
                                                <div
                                                    key={idx}
                                                    onClick={() => handleBatchSelect(batch)}
                                                    className={styles.batchOption}
                                                    style={{
                                                        padding: '12px',
                                                        border: `2px solid ${selectedBatch === batch ? 'var(--primary-color)' : 'var(--border-color)'}`,
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedBatch === batch ? 'var(--primary-light)' : 'var(--content-bg)',
                                                        transition: 'all 0.2s ease',
                                                        opacity: isBatchExpired ? 0.6 : 1
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                                                        <div>
                                                            <strong style={{ fontSize: '1rem' }}>
                                                                {isNoLot ? `📦 ${t('pages.stock.outflow.noLotNumber', 'Sem Número de Lote')}` : `🏷️ ${batch.lotNumber}`}
                                                            </strong>
                                                            {batch.expirationDate && (
                                                                <span style={{
                                                                    marginLeft: '12px',
                                                                    fontSize: '0.85rem',
                                                                    color: isBatchExpired ? '#c62828' : '#2e7d32'
                                                                }}>
                                                                    <FiCalendar size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                                                    {t('pages.stock.outflow.expirationLabel', 'Validade: {date}', { date: formatDate(batch.expirationDate) })}
                                                                    {isBatchExpired && ` ⚠️ ${t('pages.stock.outflow.expired', 'VENCIDO')}`}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                                            <FiPackage size={14} style={{ display: 'inline', marginRight: '4px' }} />
                                                            {t('pages.stock.outflow.totalLabel', 'Total: {qty} kg/L/un', { qty: batch.totalQuantity.toFixed(3) })}
                                                        </div>
                                                    </div>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                                        {t('pages.stock.outflow.locationsLabel', 'Localizações: {locations}', { locations: batch.locations.map(l => l.locationId).join(', ') })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {selectedBatch && (
                                        <button
                                            type="button"
                                            onClick={() => setShowBatchSelector(true)}
                                            className={styles.secondaryButton}
                                            style={{ marginTop: '12px' }}
                                        >
                                            <FiRefreshCw size={14} /> {t('pages.stock.outflow.changeBatch', 'Trocar Lote')}
                                        </button>
                                    )}
                                </div>
                            ) : selectedBatch && (
                                <div className={styles.selectedBatchInfo}>
                                    <div style={{
                                        padding: '12px',
                                        backgroundColor: 'var(--success-light)',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        gap: '8px'
                                    }}>
                                        <div>
                                            <FiCheck size={16} style={{ color: 'var(--success-color)', display: 'inline', marginRight: '8px' }} />
                                            <strong>{t('pages.stock.outflow.selectedBatchLabel', 'Lote selecionado:')} </strong>
                                            <span style={{ fontWeight: 'bold' }}>
                                                {selectedBatch.lotNumber || t('pages.stock.outflow.noLotNumber', 'Sem Número de Lote')}
                                            </span>
                                            {selectedBatch.expirationDate && (
                                                <span style={{ marginLeft: '12px', fontSize: '0.85rem' }}>
                                                    <FiCalendar size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                                    {t('pages.stock.outflow.expirationLabel', 'Validade: {date}', { date: formatDate(selectedBatch.expirationDate) })}
                                                    {isExpired(selectedBatch.expirationDate) && ` ⚠️ ${t('pages.stock.outflow.expired', 'VENCIDO')}`}
                                                </span>
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowBatchSelector(true)}
                                            className={styles.actionButton}
                                            title={t('pages.stock.outflow.changeBatch', 'Trocar Lote')}
                                        >
                                            <HiOutlineSwitchVertical />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {stockLoading && selectedItemId && (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <h3 className={styles.sectionTitle}>{t('pages.stock.outflow.loadingStock', 'Carregando estoque...')}</h3>
                    </div>
                    <div className={styles.infoTextSmall} style={{ padding: '10px', textAlign: 'center' }}>
                        <FiRefreshCw size={16} className={styles.spinner} style={{ display: 'inline', marginRight: '8px' }} />
                        {t('pages.stock.outflow.checkingBatches', 'Verificando lotes disponíveis...')}
                    </div>
                </div>
            )}

            {/* Seção 4: Localizações do Lote Selecionado */}
            {selectedItemId && selectedBatch && !stockLoading && (
                <div className={styles.formSection}>
                    <div className={styles.sectionHeader}>
                        <h3 className={styles.sectionTitle}>
                            {t('pages.stock.outflow.batchLocations', 'Localizações do Lote {lot}', { lot: selectedBatch.lotNumber || t('pages.stock.outflow.noLotNumber', 'Sem Lote') })}
                        </h3>
                        <span className={styles.sectionBadge}>{t('common.required', 'Obrigatório')}</span>
                    </div>
                    <div className={styles.formGrid}>
                        <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                            {availableLocationsForBatch.length === 0 && selectedLocationIds.length === 0 ? (
                                <div className={styles.warningTextSmall} style={{ padding: '10px', background: 'var(--warning-light)', borderRadius: '8px' }}>
                                    <FiAlertCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />
                                    {t('pages.stock.outflow.noLocationsAvailable', 'Nenhuma localização disponível para este lote.')}
                                </div>
                            ) : (
                                <>
                                    {availableLocationsForBatch.length > 0 && (
                                        <select
                                            onChange={(e) => {
                                                const selectedValue = e.target.value;
                                                if (selectedValue && !selectedLocationIds.includes(selectedValue)) {
                                                    setSelectedLocationIds(prev => [...prev, selectedValue]);
                                                }
                                                e.target.selectedIndex = 0;
                                            }}
                                            className={`${styles.input} ${styles.select}`}
                                            disabled={loading || isEditing}
                                            defaultValue=""
                                        >
                                            <option value="" disabled>{t('pages.stock.outflow.selectLocation', 'Selecione uma localização para remover o estoque')}</option>
                                            {availableLocationsForBatch.map(loc => {
                                                const batchLocation = selectedBatch.locations.find(l => l.locationId === loc.id);
                                                const balance = batchLocation?.quantity || 0;
                                                const isExpiredBatch = selectedBatch.expirationDate && isExpired(selectedBatch.expirationDate);
                                                return (
                                                    <option key={loc.id} value={loc.id}>
                                                        {loc.id} - {loc.description}
                                                        {isExpiredBatch ? ` ⚠️ ${t('pages.stock.outflow.batchExpired', 'LOTE VENCIDO')}` : ''}
                                                        {` (${t('pages.stock.outflow.availableInBatch', 'Disponível neste lote: {balance}', { balance: balance.toFixed(3) })})`}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    )}

                                    {/* Display selected locations with quantity inputs */}
                                    {selectedLocationIds.length > 0 && (
                                        <div className={styles.tagContainer} style={{ marginTop: '10px' }}>
                                            {selectedLocationIds.map(id => {
                                                const location = locationsList.find(l => l.id === id);
                                                const batchLocation = selectedBatch.locations.find(l => l.locationId === id);
                                                const currentBalance = batchLocation?.quantity || 0;
                                                const error = quantityErrors[id];
                                                const isExpiredBatch = selectedBatch.expirationDate && isExpired(selectedBatch.expirationDate);
                                                return (
                                                    <div key={id} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        background: isExpiredBatch ? '#fff3e0' : '#f0f7ff',
                                                        padding: '12px',
                                                        borderRadius: '8px',
                                                        marginBottom: '8px',
                                                        flexWrap: 'wrap',
                                                        gap: '10px',
                                                        border: error ? '1px solid #dc3545' : (isExpiredBatch ? '1px solid #ff9800' : '1px solid #cce5ff')
                                                    }}>
                                                        <div style={{ minWidth: '140px' }}>
                                                            <strong style={{ fontSize: '14px' }}>{id}</strong>
                                                            {location?.categoryName && (
                                                                <div style={{ fontSize: '11px', color: '#6c757d' }}>
                                                                    {location.categoryName}
                                                                </div>
                                                            )}
                                                            <div style={{ fontSize: '11px', color: isExpiredBatch ? '#e65100' : '#28a745' }}>
                                                                {t('pages.stock.outflow.availableInBatch', 'Disponível neste lote: {balance}', { balance: formatQuantity(currentBalance) })}
                                                            </div>
                                                        </div>
                                                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <input
                                                                type="number"
                                                                step="0.001"
                                                                min="0.001"
                                                                max={currentBalance}
                                                                placeholder={t('pages.stock.outflow.quantityToRemove', 'Quantidade a remover')}
                                                                className={`${styles.input} ${error ? styles.inputError : ''}`}
                                                                style={{ flex: 1 }}
                                                                value={quantity[id] || ''}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    if (value === '' || !isNaN(parseFloat(value))) {
                                                                        handleQuantityChange(id, value);
                                                                    }
                                                                }}
                                                                required
                                                                disabled={loading}
                                                            />
                                                            <span className={styles.unitText} style={{ fontSize: '12px', color: '#6c757d' }}>{t('pages.stock.outflow.unitKg', 'kg/L/un')}</span>
                                                        </div>
                                                        {!isEditing && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleRemoveLocation(id)}
                                                                className={styles.tagRemoveButton}
                                                                disabled={loading}
                                                                style={{
                                                                    background: '#dc3545',
                                                                    color: 'white',
                                                                    padding: '6px 12px',
                                                                    borderRadius: '4px',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px'
                                                                }}
                                                            >
                                                                <FiX size={14} /> {t('common.remove', 'Remover')}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}

                            {selectedLocationIds.length === 0 && selectedItemId && !stockLoading && availableLocationsForBatch.length === 0 && (
                                <small className={styles.errorTextSmall}>{t('pages.stock.outflow.locationRequired', 'Pelo menos uma Localização é obrigatória.')}</small>
                            )}

                            {selectedLocationIds.length > 0 && totalOutflow > 0 && (
                                <div className={styles.totalQuantityRow} style={{ marginTop: '12px', padding: '8px', background: '#e9ecef', borderRadius: '4px' }}>
                                    <span style={{ fontWeight: 'bold' }}>{t('pages.stock.outflow.totalToRemove', 'Total a remover:')} </span>
                                    <span style={{ color: '#dc3545', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                        {totalOutflow.toFixed(3)} {t('pages.stock.outflow.unitKg', 'kg/L/un')}
                                    </span>
                                    {selectedBatch?.lotNumber && (
                                        <span style={{ marginLeft: '12px', fontSize: '0.8rem', color: '#666' }}>
                                            <FiTag size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                            ({t('pages.stock.outflow.batchLabel', 'Lote: {lot}', { lot: selectedBatch.lotNumber })})
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Seção 5: Observações */}
            <div className={styles.formSection}>
                <div className={styles.sectionHeader}>
                    <div className={styles.sectionIcon}>
                        <FiClipboard size={20} />
                    </div>
                    <h3 className={styles.sectionTitle}>{t('pages.stock.outflow.notesSection', 'Observações')}</h3>
                    <span className={styles.sectionBadge}>{t('pages.stock.outflow.optionalBadge', 'Opcional')}</span>
                </div>
                <div className={styles.formGrid}>
                    <div className={styles.inputGroup} style={{ gridColumn: 'span 2' }}>
                        <textarea
                            id="notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className={styles.textarea}
                            rows="3"
                            disabled={loading}
                            placeholder={isEditing
                                ? t('pages.stock.outflow.editNotesPlaceholder', 'Descreva o motivo da alteração da quantidade de saída.')
                                : t('pages.stock.outflow.notesPlaceholder', 'Descreva o destino (Produção X, Venda Y, Descarte) ou outras notas.')}
                        />
                        <small className={styles.fieldHint}>
                            {isEditing ? t('pages.stock.outflow.editNotesHint', 'Justificativa para edição da quantidade') : t('pages.stock.outflow.notesHint', 'Informações complementares sobre esta saída')}
                        </small>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- Componente Principal ---
const StockOutflowPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('saida_de_estoque');
    const [rawMaterials, setRawMaterials] = useState([]);
    const [products, setProducts] = useState([]);
    const [itemsList, setItemsList] = useState([]);
    const [locations, setLocations] = useState([]);
    const [stockOutflows, setStockOutflows] = useState([]);
    const [entryToEdit, setEntryToEdit] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState('');
    const [selectedLocationIds, setSelectedLocationIds] = useState([]);
    const [quantity, setQuantity] = useState({});
    const [notes, setNotes] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [status, setStatus] = useState({ message: '', type: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [productTypes, setProductTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [filterProductTypeId, setFilterProductTypeId] = useState('');
    const [filterCategoryId, setFilterCategoryId] = useState('');
    const [selectedBatch, setSelectedBatch] = useState(null);

    // Delete confirm modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Mounted ref and timeout refs for cleanup
    const isMounted = useRef(true);
    const timeoutRefs = useRef([]);

    const { currentUser } = useAuth();
    const USER_EMAIL = currentUser ? currentUser.email : 'sistema';

    const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_DELAY);

    // Use useMemo for collection references to prevent re-renders
    const outflowsCollectionRef = useMemo(() => collection(db, STOCK_OUTFLOWS_COLLECTION), []);
    const currentStockCollectionRef = useMemo(() => collection(db, CURRENT_STOCK_COLLECTION), []);
    const rawMaterialsCollectionRef = useMemo(() => collection(db, RAW_MATERIALS_COLLECTION), []);
    const productsCollectionRef = useMemo(() => collection(db, PRODUCTS_COLLECTION), []);
    const locationsCollectionRef = useMemo(() => collection(db, LOCATIONS_COLLECTION), []);
    const productTypesCollectionRef = useMemo(() => collection(db, PRODUCT_TYPE_COLLECTION_PATH), []);
    const categoriesCollectionRef = useMemo(() => collection(db, CATEGORY_COLLECTION_PATH), []);

    const formatQuantity = (value) => {
        if (value === undefined || value === null || isNaN(value)) {
            return '0.000';
        }
        return value.toFixed(3);
    };

    // Wrap fetchEssentialData with useCallback
    const fetchEssentialData = useCallback(async () => {
        // Check if component is still mounted
        if (!isMounted.current) return;

        const cacheKey = 'stock-outflow-static-data';
        const cachedData = cache.get(cacheKey);

        if (cachedData) {
            setRawMaterials(cachedData.rawMaterials);
            setProducts(cachedData.products);
            setLocations(cachedData.locations);
            setProductTypes(cachedData.productTypes);
            setCategories(cachedData.categories);

            const combined = [
                ...cachedData.rawMaterials.map(item => ({ ...item, isProduct: false })),
                ...cachedData.products.map(item => ({ ...item, isProduct: true }))
            ];
            combined.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
            setItemsList(combined);

            // Set loading to false when cached data is used
            if (isMounted.current) {
                setInitialLoading(false);
            }
            return;
        }

        try {
            const [mpData, prodData, locData, typesData, catData] = await Promise.all([
                getDocs(rawMaterialsCollectionRef),
                getDocs(productsCollectionRef),
                getDocs(locationsCollectionRef),
                getDocs(productTypesCollectionRef),
                getDocs(categoriesCollectionRef)
            ]);

            // Only update state if component is still mounted
            if (!isMounted.current) return;

            let fetchedRawMaterials = mpData.docs.map(doc => ({ id: doc.id, ...doc.data(), isProduct: false }));
            fetchedRawMaterials.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
            setRawMaterials(fetchedRawMaterials);

            let fetchedProducts = prodData.docs.map(doc => ({ id: doc.id, ...doc.data(), isProduct: true }));
            fetchedProducts.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
            setProducts(fetchedProducts);

            const combined = [...fetchedRawMaterials, ...fetchedProducts];
            combined.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
            setItemsList(combined);

            let fetchedLocations = locData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedLocations.sort((a, b) => (a.description || '').localeCompare(b.description || ''));
            setLocations(fetchedLocations);

            let fetchedProductTypes = typesData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedProductTypes.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setProductTypes(fetchedProductTypes);

            let fetchedCategories = catData.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedCategories.sort((a, b) => (a.order || 0) - (b.order || 0));
            setCategories(fetchedCategories);

            cache.set(cacheKey, {
                rawMaterials: fetchedRawMaterials,
                products: fetchedProducts,
                locations: fetchedLocations,
                productTypes: fetchedProductTypes,
                categories: fetchedCategories
            });

        } catch (error) {
            console.error("Erro ao buscar dados essenciais:", error);
            if (isMounted.current) {
                setStatus({ message: t('pages.stock.outflow.loadError', 'Erro ao carregar itens ou localizações.'), type: 'error' });
            }
        } finally {
            // Set initial loading to false after data is fetched (whether success or error)
            // Only if component is still mounted
            if (isMounted.current) {
                setInitialLoading(false);
            }
        }
    }, [rawMaterialsCollectionRef, productsCollectionRef, locationsCollectionRef, productTypesCollectionRef, categoriesCollectionRef, t]);

    // Cleanup effect
    useEffect(() => {
        // Set isMounted to true when component mounts
        isMounted.current = true;

        return () => {
            isMounted.current = false;
            // Clear all timeouts
            timeoutRefs.current.forEach(clearTimeout);
            timeoutRefs.current = [];
            // Clear status
            setStatus({ message: '', type: '' });
        };
    }, []);

    // Fetch essential data on mount
    useEffect(() => {
        let mounted = true;

        const loadData = async () => {
            if (mounted) {
                await fetchEssentialData();
            }
        };

        loadData();

        return () => {
            mounted = false;
        };
    }, [fetchEssentialData]);

    // Real-time listener for stock outflows
    useEffect(() => {
        const q = query(outflowsCollectionRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedOutflows = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(entry => entry.quantity !== undefined && entry.quantity !== null);

            if (isMounted.current) {
                setStockOutflows(fetchedOutflows);
                setTotalCount(fetchedOutflows.length);
                setInitialLoading(false);
            }
        }, (error) => {
            console.error("Erro ao buscar saídas de estoque em tempo real:", error);
            if (isMounted.current) {
                setStatus({ message: t('pages.stock.outflow.historyLoadError', 'Erro ao carregar histórico de saídas.'), type: 'error' });
                setInitialLoading(false);
            }
        });

        return () => unsubscribe();
    }, [outflowsCollectionRef, t]);

    const handleResetForm = () => {
        setSelectedItemId('');
        setSelectedLocationIds([]);
        setQuantity({});
        setNotes('');
        setStatus({ message: '', type: '' });
        setEntryToEdit(null);
        setFilterProductTypeId('');
        setFilterCategoryId('');
        setSelectedBatch(null);
    };

    const handleCloseModal = () => {
        handleResetForm();
        setIsModalOpen(false);
        setStatus({ message: '', type: '' });
    };

    const handleAddClick = () => {
        handleResetForm();
        setIsModalOpen(true);
    };

    const handleEditClick = (entry) => {
        setEntryToEdit(entry);
        setSelectedItemId(entry.rawMaterialId);
        setSelectedLocationIds(entry.locationIds || []);
        if (entry.locationQuantities) {
            setQuantity(entry.locationQuantities);
        } else {
            const qtyObj = {};
            (entry.locationIds || []).forEach(locId => {
                qtyObj[locId] = (entry.quantity / (entry.locationIds?.length || 1)).toFixed(3);
            });
            setQuantity(qtyObj);
        }
        setNotes(entry.notes || '');
        setStatus({ message: '', type: '' });
        setIsModalOpen(true);
    };

    const handleDeleteClick = (entry) => {
        setDeleteTarget(entry);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        const entry = deleteTarget;
        if (!entry) return;

        setIsDeleting(true);
        try {
            await handleDelete(entry);
            setDeleteModalOpen(false);
            setIsDeleting(false);
        } catch (error) {
            setIsDeleting(false);
        }
    };

    const handleSave = (entryId) => {
        if (entryId) {
            handleUpdate(entryId);
        } else {
            handleCreate();
        }
    };

    // Helper to safely set status
    const safeSetStatus = (newStatus) => {
        if (isMounted.current) {
            setStatus(newStatus);
        }
    };

    // Helper to safely set timeout
    const safeSetTimeout = (callback, delay) => {
        const id = setTimeout(() => {
            if (isMounted.current) {
                callback();
            }
        }, delay);
        timeoutRefs.current.push(id);
        return id;
    };

    const handleCreate = async () => {
        setLoading(true);
        safeSetStatus({ message: '', type: '' });

        const selectedItem = itemsList.find(r => r.id === selectedItemId);
        const itemName = selectedItem ? (selectedItem.commercialName || selectedItem.name || '') : '';

        const totalOutflow = selectedLocationIds.reduce((acc, id) => acc + (parseFloat(quantity[id]) || 0), 0);
        const hasInvalidQty = selectedLocationIds.some(id => !quantity[id] || parseFloat(quantity[id]) <= 0);

        if (!selectedItem || !selectedBatch || selectedLocationIds.length === 0 || hasInvalidQty) {
            safeSetStatus({ message: t('pages.stock.outflow.createValidationError', 'Informe o Item, selecione o Lote e as quantidades para todas as localizações.'), type: 'error' });
            setLoading(false);
            return;
        }

        try {
            await runTransaction(db, async (transaction) => {
                const stockDataToUpdate = [];

                for (const locationId of selectedLocationIds) {
                    const batchLocation = selectedBatch.locations.find(l => l.locationId === locationId);
                    if (!batchLocation) {
                        throw new Error(t('pages.stock.outflow.locationNotFoundInBatch', 'Localização {location} não encontrada no lote selecionado.', { location: locationId }));
                    }

                    const currentStockRef = doc(currentStockCollectionRef, batchLocation.stockId);
                    const stockDoc = await transaction.get(currentStockRef);

                    if (!stockDoc.exists()) {
                        throw new Error(t('pages.stock.outflow.stockNotFound', 'Estoque não encontrado para {location} no lote {lot}.', {
                            location: locationId,
                            lot: selectedBatch.lotNumber || t('pages.stock.outflow.noLotNumber', 'sem lote')
                        }));
                    }

                    const currentQuantity = stockDoc.data().quantity || 0;
                    const amountToRemove = parseFloat(quantity[locationId]);
                    const newQuantity = currentQuantity - amountToRemove;

                    if (newQuantity < 0) {
                        throw new Error(t('pages.stock.outflow.insufficientBalanceDetail', 'Saldo insuficiente em {location} para o lote {lot}. Atual: {current}, Solicitado: {requested}', {
                            location: locationId,
                            lot: selectedBatch.lotNumber || t('pages.stock.outflow.noLotNumber', 'sem lote'),
                            current: currentQuantity.toFixed(3),
                            requested: amountToRemove.toFixed(3)
                        }));
                    }

                    const stockData = {
                        rawMaterialId: selectedItemId,
                        locationId: locationId,
                        quantity: newQuantity,
                        rawMaterialCode: selectedItem.code,
                        rawMaterialName: itemName,
                        locationDescription: locations.find(l => l.id === locationId)?.description || locationId,
                        updatedAt: serverTimestamp(),
                        createdAt: stockDoc.data().createdAt || serverTimestamp(),
                        lotNumber: selectedBatch.lotNumber,
                        expirationDate: selectedBatch.expirationDate,
                        manufacturerDate: selectedBatch.manufacturerDate,
                        isProduct: selectedItem.isProduct || false,
                    };

                    stockDataToUpdate.push({ ref: currentStockRef, data: stockData });
                }

                const newEntryRef = doc(outflowsCollectionRef);

                transaction.set(newEntryRef, {
                    rawMaterialId: selectedItemId,
                    rawMaterialCode: selectedItem.code,
                    rawMaterialName: itemName,
                    locationIds: selectedLocationIds,
                    locationQuantities: quantity,
                    quantity: totalOutflow,
                    type: 'SAÍDA',
                    notes: notes.trim(),
                    createdAt: serverTimestamp(),
                    createdBy: USER_EMAIL,
                    lotNumber: selectedBatch.lotNumber,
                    expirationDate: selectedBatch.expirationDate,
                    manufacturerDate: selectedBatch.manufacturerDate,
                    isProduct: selectedItem.isProduct || false,
                });

                for (const item of stockDataToUpdate) {
                    if (item.data.quantity <= 0.001) {
                        transaction.delete(item.ref);
                    } else {
                        transaction.set(item.ref, item.data);
                    }
                }
            });

            safeSetStatus({
                message: t('pages.stock.outflow.createSuccess', '✓ Saída de {quantity} registrada com sucesso! Lote: {lot}', {
                    quantity: formatQuantity(totalOutflow),
                    lot: selectedBatch.lotNumber || t('pages.stock.outflow.noLotNumber', 'Sem lote')
                }), type: 'success'
            });

            safeSetTimeout(() => {
                handleCloseModal();
                safeSetStatus({ message: '', type: '' });
            }, 1500);
        } catch (error) {
            safeSetStatus({ message: t('pages.stock.outflow.createError', '✗ {message}', { message: error.message }), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (entryId) => {
        setLoading(true);
        safeSetStatus({ message: '', type: '' });

        const oldEntry = entryToEdit;

        const newTotalOutflow = Object.values(quantity).reduce((acc, val) => acc + (parseFloat(val) || 0), 0);
        const oldTotalOutflow = parseFloat(oldEntry.quantity);

        if (selectedLocationIds.length === 0 || newTotalOutflow <= 0 || isNaN(newTotalOutflow)) {
            safeSetStatus({ message: t('pages.stock.outflow.invalidQuantity', 'A Quantidade deve ser válida (> 0).'), type: 'error' });
            setLoading(false);
            return;
        }

        if (newTotalOutflow === oldTotalOutflow && notes.trim() === (oldEntry.notes || '').trim()) {
            safeSetStatus({ message: t('pages.stock.outflow.noChanges', 'Nenhuma alteração detectada.'), type: 'info' });
            setLoading(false);
            safeSetTimeout(() => {
                handleCloseModal();
            }, 1000);
            return;
        }

        try {
            await runTransaction(db, async (transaction) => {
                const stockDataToUpdate = [];
                const locationsToUpdate = oldEntry.locationIds || [];

                for (const locationId of locationsToUpdate) {
                    let currentStockId;
                    if (oldEntry.isProduct && oldEntry.lotNumber) {
                        currentStockId = `${oldEntry.rawMaterialId}_${locationId}_${oldEntry.lotNumber}`;
                    } else {
                        currentStockId = `${oldEntry.rawMaterialId}_${locationId}`;
                    }

                    const currentStockRef = doc(currentStockCollectionRef, currentStockId);
                    const stockDoc = await transaction.get(currentStockRef);

                    if (!stockDoc.exists()) {
                        throw new Error(t('pages.stock.outflow.stockNotFoundForEdit', 'Estoque atual para {location} não encontrado. Não é possível editar.', { location: locationId }));
                    }

                    const currentQuantity = stockDoc.data().quantity || 0;

                    const oldLocationQuantity = oldEntry.locationQuantities?.[locationId] || (oldTotalOutflow / locationsToUpdate.length);
                    const newLocationQuantity = parseFloat(quantity[locationId]) || 0;

                    const newQuantity = currentQuantity + oldLocationQuantity - newLocationQuantity;

                    if (newQuantity < 0) {
                        throw new Error(t('pages.stock.outflow.negativeStockError', 'A nova quantidade de saída resulta em saldo negativo do estoque ({balance}) na localização {location}.', {
                            balance: newQuantity.toFixed(3),
                            location: locationId
                        }));
                    }

                    const stockData = {
                        rawMaterialId: oldEntry.rawMaterialId,
                        locationId: locationId,
                        quantity: newQuantity,
                        rawMaterialCode: oldEntry.rawMaterialCode,
                        rawMaterialName: oldEntry.rawMaterialName,
                        locationDescription: locations.find(l => l.id === locationId)?.description || locationId,
                        updatedAt: serverTimestamp(),
                        createdAt: stockDoc.data().createdAt,
                        lotNumber: stockDoc.data().lotNumber || oldEntry.lotNumber,
                        expirationDate: stockDoc.data().expirationDate || oldEntry.expirationDate,
                        manufacturerDate: stockDoc.data().manufacturerDate || oldEntry.manufacturerDate,
                        isProduct: oldEntry.isProduct || false,
                    };
                    stockDataToUpdate.push({ ref: currentStockRef, data: stockData });
                }

                const entryRef = doc(outflowsCollectionRef, entryId);
                const entryData = {
                    quantity: newTotalOutflow,
                    locationQuantities: quantity,
                    notes: notes.trim(),
                    updatedAt: serverTimestamp(),
                    updatedBy: USER_EMAIL
                };
                transaction.update(entryRef, entryData);

                for (const item of stockDataToUpdate) {
                    if (item.data.quantity <= 0.001) {
                        transaction.delete(item.ref);
                    } else {
                        transaction.set(item.ref, item.data);
                    }
                }
            });

            safeSetStatus({
                message: t('pages.stock.outflow.updateSuccess', '✓ Saída {code} atualizada para {quantity} unidades!', {
                    code: oldEntry.rawMaterialCode,
                    quantity: formatQuantity(newTotalOutflow)
                }), type: 'success'
            });

            safeSetTimeout(() => {
                handleCloseModal();
            }, 1500);
        } catch (error) {
            console.error("Erro ao atualizar saída de estoque:", error);
            safeSetStatus({ message: t('pages.stock.outflow.updateError', '✗ {message}', { message: error.message }), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (entryToDelete) => {
        setLoading(true);
        safeSetStatus({ message: '', type: '' });

        const quantityToDelete = parseFloat(entryToDelete.quantity);

        try {
            await runTransaction(db, async (transaction) => {
                const stockDataToUpdate = [];
                const locationsToUpdate = entryToDelete.locationIds || [];

                for (const locationId of locationsToUpdate) {
                    let currentStockId;
                    if (entryToDelete.isProduct && entryToDelete.lotNumber) {
                        currentStockId = `${entryToDelete.rawMaterialId}_${locationId}_${entryToDelete.lotNumber}`;
                    } else {
                        currentStockId = `${entryToDelete.rawMaterialId}_${locationId}`;
                    }

                    const currentStockRef = doc(currentStockCollectionRef, currentStockId);
                    const stockDoc = await transaction.get(currentStockRef);

                    if (!stockDoc.exists()) {
                        console.warn(`Estoque atual para ${locationId} não encontrado.`);
                        continue;
                    }

                    const currentQuantity = stockDoc.data().quantity || 0;
                    const locationQuantity = entryToDelete.locationQuantities?.[locationId] || (quantityToDelete / locationsToUpdate.length);
                    const newQuantity = currentQuantity + locationQuantity;

                    const stockData = {
                        rawMaterialId: entryToDelete.rawMaterialId,
                        locationId: locationId,
                        quantity: newQuantity,
                        rawMaterialCode: entryToDelete.rawMaterialCode,
                        rawMaterialName: entryToDelete.rawMaterialName,
                        locationDescription: locations.find(l => l.id === locationId)?.description || locationId,
                        updatedAt: serverTimestamp(),
                        createdAt: stockDoc.data().createdAt,
                        lotNumber: stockDoc.data().lotNumber || entryToDelete.lotNumber,
                        expirationDate: stockDoc.data().expirationDate || entryToDelete.expirationDate,
                        manufacturerDate: stockDoc.data().manufacturerDate || entryToDelete.manufacturerDate,
                        isProduct: entryToDelete.isProduct || false,
                    };
                    stockDataToUpdate.push({ ref: currentStockRef, data: stockData });
                }

                const entryRef = doc(outflowsCollectionRef, entryToDelete.id);
                transaction.delete(entryRef);

                for (const item of stockDataToUpdate) {
                    transaction.set(item.ref, item.data);
                }
            });

            safeSetStatus({
                message: t('pages.stock.outflow.deleteSuccess', '✓ Saída {code} ({quantity}) excluída!', {
                    code: entryToDelete.rawMaterialCode,
                    quantity: formatQuantity(quantityToDelete)
                }), type: 'success'
            });

            safeSetTimeout(() => {
                safeSetStatus({ message: '', type: '' });
            }, 3000);
        } catch (error) {
            console.error("Erro ao excluir saída de estoque:", error);
            safeSetStatus({ message: t('pages.stock.outflow.deleteError', '✗ Erro ao excluir saída: {message}', { message: error.message }), type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const filteredEntries = useMemo(() => {
        if (!stockOutflows) return [];
        if (!debouncedSearchTerm) return stockOutflows;

        const lowerCaseSearchTerm = debouncedSearchTerm.toLowerCase();

        return stockOutflows.filter(item => {
            const code = (item.rawMaterialCode || '').toLowerCase();
            const name = (item.rawMaterialName || '').toLowerCase();
            const notes = (item.notes || '').toLowerCase();
            const createdBy = (item.createdBy || '').toLowerCase();
            const lotNumber = (item.lotNumber || '').toLowerCase();

            return code.includes(lowerCaseSearchTerm) ||
                name.includes(lowerCaseSearchTerm) ||
                notes.includes(lowerCaseSearchTerm) ||
                createdBy.includes(lowerCaseSearchTerm) ||
                lotNumber.includes(lowerCaseSearchTerm);
        });
    }, [stockOutflows, debouncedSearchTerm]);

    const totalFilteredItems = filteredEntries.length;
    const totalPages = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentEntries = useMemo(() => {
        const startIndex = (page - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredEntries.slice(startIndex, endIndex);
    }, [filteredEntries, page]);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
            return date.toLocaleString('pt-BR');
        } catch {
            return 'Data inválida';
        }
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        setPage(1);
    };

    return (
        <div className={styles.pageContainer}>
            {/* Outflow Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={(e) => handleSave(entryToEdit?.id)}
                isLoading={loading}
                error={status.type === 'error' ? status.message : ''}
                success={status.type === 'success' ? status.message : ''}
                title={entryToEdit ? t('pages.stock.outflow.editTitle', 'Editar Saída de Estoque') : t('pages.stock.outflow.newTitle', 'Registrar Saída de Estoque')}
                subtitle={entryToEdit
                    ? t('pages.stock.outflow.editSubtitle', 'Editando saída de {code} - {name}', { code: entryToEdit?.rawMaterialCode || '', name: entryToEdit?.rawMaterialName || '' })
                    : t('pages.stock.outflow.newSubtitle', 'Preencha os dados abaixo para registrar a saída de materiais do estoque')}
                submitText={entryToEdit ? t('common.save', 'Salvar Alterações') : t('pages.stock.outflow.registerButton', 'Registrar Saída')}
                maxWidth="700px"
            >
                <StockOutflowModalContent
                    isEditing={!!entryToEdit}
                    itemsList={itemsList}
                    locationsList={locations}
                    selectedItemId={selectedItemId}
                    setSelectedItemId={setSelectedItemId}
                    selectedLocationIds={selectedLocationIds}
                    setSelectedLocationIds={setSelectedLocationIds}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    notes={notes}
                    setNotes={setNotes}
                    status={status}
                    productTypes={productTypes}
                    categories={categories}
                    filterProductTypeId={filterProductTypeId}
                    setFilterProductTypeId={setFilterProductTypeId}
                    filterCategoryId={filterCategoryId}
                    setFilterCategoryId={setFilterCategoryId}
                    selectedBatch={selectedBatch}
                    setSelectedBatch={setSelectedBatch}
                    loading={loading}
                    t={t}
                />
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setDeleteTarget(null);
                }}
                onConfirm={handleConfirmDelete}
                isLoading={isDeleting}
                title={t('pages.stock.outflow.deleteTitle', 'Excluir Saída')}
                message={t('pages.stock.outflow.deleteMessage', 'Tem certeza que deseja excluir a saída {code} ({quantity})? Esta ação é irreversível.', {
                    code: deleteTarget?.rawMaterialCode || '',
                    quantity: (deleteTarget?.quantity || 0).toFixed(3)
                })}
                confirmText={t('common.delete', 'Excluir')}
                cancelText={t('common.cancel', 'Cancelar')}
                danger
            />

            {/* Page Header */}
            <PageHeader
                title={t('pages.stock.outflow.title', 'Histórico de Saídas de Estoque')}
                actionText={can.create ? t('pages.stock.outflow.registerButton', 'Registrar Saída') : undefined}
                onAction={can.create ? handleAddClick : undefined}
                disabled={loading || initialLoading}
                showAction={!!can.create}
            />

            {/* Status Toast */}
            {status.message && !isModalOpen && (
                <Toast
                    message={status.message}
                    type={status.type}
                    onClose={() => setStatus({ message: '', type: '' })}
                />
            )}

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <div className={styles.inputGroup}>
                    <label>{t('common.search', 'Buscar')}</label>
                    <SearchBar
                        placeholder={t('pages.stock.outflow.searchPlaceholder', 'Buscar por código, nome, lote, observação ou usuário...')}
                        onSearch={handleSearch}
                        disabled={initialLoading}
                        debounceDelay={DEBOUNCE_DELAY}
                    />
                </div>
            </div>

            {/* Table */}
            <div className={styles.tableCard}>
                <div className={styles.tableContainer}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: '150px' }}>{t('pages.stock.outflow.tableDate', 'Data')}</th>
                                <th style={{ width: '100px' }}>{t('pages.stock.outflow.tableCode', 'Código')}</th>
                                <th>{t('pages.stock.outflow.tableItem', 'Item')}</th>
                                <th style={{ width: '100px' }}>{t('pages.stock.outflow.tableLot', 'Lote')}</th>
                                <th style={{ width: '100px' }}>{t('pages.stock.outflow.tableExpiration', 'Validade')}</th>
                                <th style={{ width: '180px' }}>{t('pages.stock.outflow.tableLocations', 'Localizações')}</th>
                                <th style={{ width: '100px' }}>{t('pages.stock.outflow.tableQuantity', 'Quantidade')}</th>
                                <th>{t('pages.stock.outflow.tableNotes', 'Observação')}</th>
                                <th style={{ width: '130px' }}>{t('pages.stock.outflow.tableUser', 'Usuário')}</th>
                                <th style={{ width: '120px' }}>{t('pages.stock.outflow.tableActions', 'Ações')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {initialLoading && stockOutflows.length === 0 ? (
                                <TableSkeleton columns={10} rows={5} />
                            ) : currentEntries.length === 0 ? (
                                <tr>
                                    <td colSpan={10}>
                                        <EmptyState
                                            title={debouncedSearchTerm
                                                ? t('pages.stock.outflow.noResults', 'Nenhuma saída encontrada para "{term}"', { term: debouncedSearchTerm })
                                                : t('pages.stock.outflow.noOutflows', 'Nenhuma saída de estoque registrada')}
                                            message={debouncedSearchTerm ? t('pages.stock.outflow.tryDifferentSearch', 'Tente ajustar os filtros de busca') : t('pages.stock.outflow.startAdding', 'Comece registrando uma nova saída')}
                                            actionText={can.create ? t('pages.stock.outflow.registerButton', 'Registrar Saída') : undefined}
                                            onAction={can.create ? handleAddClick : undefined}
                                        />
                                    </td>
                                </tr>
                            ) : (
                                currentEntries.map((entry) => (
                                    <tr key={entry.id} className={styles.tableRow}>
                                        <td>
                                            {formatTimestamp(entry.updatedAt || entry.createdAt)}
                                            {entry.updatedAt && <span style={{ display: 'block', fontSize: '0.7rem', color: 'var(--secondary-color)' }}>({t('pages.stock.outflow.edited', 'Editado')})</span>}
                                        </td>
                                        <td>{entry.rawMaterialCode}</td>
                                        <td>{entry.rawMaterialName}</td>
                                        <td>{entry.lotNumber || '—'}</td>
                                        <td>{entry.expirationDate ? formatDate(entry.expirationDate) : '—'}</td>
                                        <td>
                                            {entry.locationIds?.slice(0, 3).map(id => (
                                                <span key={id} className={styles.tagSmall}>
                                                    {id}
                                                </span>
                                            ))}
                                            {entry.locationIds?.length > 3 && (
                                                <span className={styles.tagSmall}>+{entry.locationIds.length - 3}</span>
                                            )}
                                        </td>
                                        <td style={{ fontWeight: 'bold', color: 'var(--danger-color)' }}>
                                            {formatQuantity(entry.quantity)}
                                        </td>
                                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={entry.notes}>
                                            {entry.notes || '—'}
                                        </td>
                                        <td>
                                            {entry.updatedBy || entry.createdBy || 'N/A'}
                                        </td>
                                        <td style={{ textAlign: 'center' }} className={styles.actionCell}>
                                            {can.edit && (
                                                <button
                                                    onClick={() => handleEditClick(entry)}
                                                    className={`${styles.actionButton} ${styles.editButton}`}
                                                    disabled={loading}
                                                    title={t('common.edit', 'Editar')}
                                                >
                                                    <FiEdit size={18} />
                                                </button>
                                            )}
                                            {can.delete && (
                                                <button
                                                    onClick={() => handleDeleteClick(entry)}
                                                    className={`${styles.actionButton} ${styles.deleteButton}`}
                                                    disabled={loading}
                                                    title={t('common.delete', 'Excluir')}
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalFilteredItems}
                    onPageChange={handlePageChange}
                    disabled={initialLoading}
                    itemsLabel={t('pages.stock.outflow.movements', 'movimentos')}
                />
            </div>
        </div>
    );
};

export default StockOutflowPage;